import { CartAddEvent } from '@theme/events';

function normalizeSizeKey(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/\buk\b/g, '')
    .replace(/cover only/g, '')
    .replace(/cm\b/g, 'cm')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function selectedCoverSizeKey(bundle) {
  const optionInputs = Array.from(document.querySelectorAll('[data-dv-option]:checked'));
  const sizeInput = optionInputs.find((input) => {
    const optionName = `${input.dataset.optionName || ''} ${input.name || ''}`.toLowerCase();
    return optionName.includes('size') || optionName.includes('dimension') || optionName.includes('specification');
  });
  const sizeValue =
    sizeInput?.getAttribute('data-dv-option-display-value') ||
    sizeInput?.value ||
    bundle?.dataset.dvInsertSelectedSize ||
    '';
  return normalizeSizeKey(sizeValue);
}

function reorderBundleCards(bundle) {
  const track = bundle.querySelector('[data-dv-insert-bundle-track]');
  if (!track) return;

  const selectedSize = selectedCoverSizeKey(bundle);
  if (!selectedSize) return;

  const cards = Array.from(track.querySelectorAll('[data-dv-insert-card]'));
  const matching = [];
  const remaining = [];

  cards.forEach((card) => {
    const cardSizeKey = card.getAttribute('data-dv-insert-size-key');
    if (cardSizeKey === selectedSize) matching.push(card);
    else remaining.push(card);
  });

  [...matching, ...remaining].forEach((card) => track.appendChild(card));
  track.scrollLeft = 0;
  track.dispatchEvent(new Event('scroll'));
  bundle.dataset.dvInsertSelectedSize = selectedSize;
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, options);
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Unable to add insert to cart.');
  }
  return data;
}

async function fetchCart() {
  try {
    return await fetchJson('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });
  } catch {
    return null;
  }
}

function updateCartCounts(cart) {
  if (!cart || typeof cart.item_count !== 'number') return;
  const count = String(cart.item_count);
  document.querySelectorAll('.dv-cart-count').forEach((element) => {
    element.textContent = count;
  });
  document.querySelectorAll('.cart-bubble__text-count').forEach((element) => {
    element.textContent = cart.item_count > 99 ? '' : count;
    element.classList.toggle('hidden', cart.item_count === 0);
  });
  document.querySelectorAll('.cart-bubble').forEach((element) => {
    element.classList.toggle('visually-hidden', cart.item_count === 0);
  });
  sessionStorage.setItem('cart-count', count);
}

async function submitInsertOfferForm(form) {
  const button = form.querySelector('button[type="submit"]');
  const originalText = button?.textContent || '';
  const formData = new FormData(form);
  const sectionIds = cartSectionIds();

  if (sectionIds.length) {
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
  }

  if (button) {
    button.disabled = true;
    button.textContent = 'Adding';
  }

  try {
    const response = await fetchJson(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });
    const cart = await fetchCart();
    updateCartCounts(cart);

    document.dispatchEvent(
      new CartAddEvent(cart || response, form.id || 'dv-cushion-insert-offer', {
        source: 'dv-cushion-insert-offer',
        itemCount: cart?.item_count,
        variantId: String(formData.get('id') || ''),
        sections: response.sections || {},
      })
    );

    if (button) button.textContent = 'Added';
  } catch (error) {
    console.error(error);
    if (button) button.textContent = 'Try Again';
  } finally {
    window.setTimeout(() => {
      if (!button) return;
      button.disabled = false;
      button.textContent = originalText || 'Add Insert';
    }, 1200);
  }
}

function initCushionInsertBundles(root = document) {
  root.querySelectorAll('[data-dv-insert-bundle]').forEach(reorderBundleCards);
}

function adjustInsertQuantity(button) {
  const control = button.closest('.dv-cushion-insert-card__quantity-control');
  const input = control?.querySelector('input[name="quantity"]');
  if (!(input instanceof HTMLInputElement)) return;

  const min = Number(input.min || 1);
  const max = Number(input.max || 20);
  const current = Number(input.value || min);
  const direction = button.getAttribute('data-dv-insert-quantity-step') === 'increase' ? 1 : -1;
  const next = Math.min(max, Math.max(min, current + direction));
  input.value = String(next);
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches('[data-dv-option]')) return;
  initCushionInsertBundles(document);
});

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-dv-insert-quantity-step]') : null;
  if (!(button instanceof HTMLButtonElement)) return;
  adjustInsertQuantity(button);
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-dv-insert-offer-form]')) return;
  event.preventDefault();
  submitInsertOfferForm(form);
});

document.addEventListener('DOMContentLoaded', () => initCushionInsertBundles(document));
document.addEventListener('shopify:section:load', (event) => initCushionInsertBundles(event.target));
